import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  FiservCard,
  FiservCardInterface,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { ROUTES } from '@/common';
import { useHistory as useCustomHistory } from '@/modules/core/hooks';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import theme from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import { useSearchParams } from '../../Cards/hooks/useSearchParams';
import { useGetCardsQuery } from '../../Cards/queries/useGetCards.query';
import CardDetailsTable from '../CardDetailsTable';

import { StyledCarousel, StyledCarouselButton, StyledCarouselWrapper } from './CardCarousel.styles';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

const getParams = (cardId: string) =>
  new URLSearchParams({
    card_id: cardId,
  });

interface Props {
  employeeId: string;
}
const CARD_ID_KEY = 'card_id';
export const CardCarousel: React.FC<Props> = ({ employeeId }) => {
  const { push, replace } = useHistory();
  const { pathname, hash } = useLocation();
  const { observingSearchQuery, push: customPush } = useCustomHistory();
  const { observingOrganizationData } = useOrganizationOverridableProperties();

  const currentPath = useMemo(() => `${pathname}?${observingSearchQuery}`, [observingSearchQuery, pathname]);

  const pushWithParams = useCallback(
    (cardId: string) => {
      const params = getParams(cardId);
      push(`${currentPath}&${params.toString()}${hash}`);
    },
    [push, hash, currentPath]
  );

  const replaceWithParams = useCallback(
    (cardId: string) => {
      const params = getParams(cardId);
      replace(`${currentPath}&${params.toString()}${hash}`);
    },
    [replace, hash, currentPath]
  );

  const { query: cardId } = useSearchParams(CARD_ID_KEY);

  const handleOpenCardPage = useCallback(
    (currentCardId: string) => {
      if (employeeId && currentCardId) {
        customPush(ROUTES.CARD_DETAILS_BY_ID(employeeId, currentCardId));
      }
    },
    [customPush, employeeId]
  );

  const { cardMap, isLoading } = useGetCardsQuery(employeeId);
  const [activeCard, setActiveCard] = useState<FiservCardInterface | undefined | null>(null);

  const cards: FiservCardInterface[] = useMemo(() => {
    const fields: FiservCardInterface[] = [];
    Object.keys(cardMap).forEach((key) => {
      const field = cardMap[key];
      Object.keys(field).forEach((newKey) => {
        const list = _.get(field, `${newKey}`) as FiservCardInterface[];
        list.forEach((item) => {
          fields.push(item);
        });
      });
    });

    return fields;
  }, [cardMap]);

  const selectedItemIndex = useMemo(() => {
    const index = cards.findIndex((card) => card.id === cardId);
    return index > -1 ? index : 0;
  }, [cardId, cards]);

  const goNextHandler = useCallback(
    (type: 'prev' | 'next') => {
      if (!activeCard) return;
      if (type === 'next' && cards[selectedItemIndex + 1]) {
        pushWithParams(cards[selectedItemIndex + 1].id);
      }

      if (type === 'prev' && cards[selectedItemIndex - 1]) {
        pushWithParams(cards[selectedItemIndex - 1].id);
      }
    },
    [activeCard, cards, selectedItemIndex, pushWithParams]
  );

  useEffect(() => {
    if (cards.length && selectedItemIndex >= 0) {
      const nextCardId = cards[selectedItemIndex].id;
      setActiveCard(cards[selectedItemIndex]);

      if (nextCardId !== cardId) {
        replaceWithParams(nextCardId);
      }
    }
  }, [cardId, cards, currentPath, isLoading, replaceWithParams, selectedItemIndex, hash]);

  return (
    <Box background="module" round="moduleRound" pad="spacing24">
      <Box direction="row" justify="between">
        <Text size="20px" margin={{ bottom: 'spacing16' }} data-testid="CardCarousel-title">
          Cards: {cards.length}
        </Text>
        <Box direction="row">
          <StyledCarouselButton
            margin={{ right: 'spacing8' }}
            onClick={() => goNextHandler('prev')}
            disabled={!cards[selectedItemIndex - 1]}
          >
            <ChevronLeftIcon size="12px" className="arrow-icon" color="iconPrimary" />
          </StyledCarouselButton>
          <StyledCarouselButton
            onClick={() => goNextHandler('next')}
            disabled={!cards[selectedItemIndex + 1]}
          >
            <ChevronRightIcon size="12px" className="arrow-icon" color="iconPrimary" />
          </StyledCarouselButton>
        </Box>
      </Box>
      {cards?.length ? (
        <StyledCarouselWrapper data-testid="CardCarousel-carousel-wrapper">
          <StyledCarousel
            centerMode
            centerSlidePercentage={49}
            renderArrowNext={() => null}
            renderArrowPrev={() => null}
            showIndicators={false}
            showStatus={false}
            selectedItem={selectedItemIndex}
            showThumbs={false}
            className={selectedItemIndex > 0 ? 'passed' : undefined}
          >
            {[
              ...cards.map((card, index) => (
                <FiservCard
                  key={card.id}
                  card={card}
                  isActive={index === selectedItemIndex}
                  logos={{
                    logoUrl: observingOrganizationData?.logoInfo?.darkLargeLogoUrl,
                    cardLogo: whiteLabelingConfig?.images?.cardLogo,
                    defaultCardLogo: whiteLabelingConfig?.logo,
                    cardBackground: whiteLabelingConfig?.images?.cardBackground,
                  }}
                  isLoading={isLoading}
                  onPushWithParams={(_, cardId) => pushWithParams(cardId)}
                  wrapperStyle={{
                    marginBottom: theme.spacings.l,
                    marginTop: theme.spacings.s,
                    marginRight: theme.spacings.s,
                    marginLeft: theme.spacings.s,
                    maxWidth: '490px',
                  }}
                />
              )),
              <Box key="empty" />,
            ]}
          </StyledCarousel>
        </StyledCarouselWrapper>
      ) : (
        <Box justify="center" align="center">
          <Text color="textSecondary" size="14px" weight={400} margin={{ vertical: 's' }}>
            The employee doesn't have any cards yet.
          </Text>
        </Box>
      )}
      {activeCard && (
        <CardDetailsTable
          card={activeCard}
          onClick={() => {
            handleOpenCardPage(activeCard.id);
          }}
        />
      )}
    </Box>
  );
};
